import { useState } from "react";
import { Badge, Button, Card, Group, Modal, Stack, Text, Textarea, Title } from "@mantine/core";
import { formatKr } from "@nodus/booking";
import type { BookingApproval } from "@/lib/api/booking-approvals";
import { clock, dayClock } from "@/lib/clock";
import { lapsesAtStart } from "@/lib/booking-approvals";

export interface ApprovalRequestsCardProps {
  requests: BookingApproval[];
  /** How many are waiting in all — ``requests`` is the first page (#434). Defaults to ``requests.length``. */
  total?: number;
  /** The request a notification link opened: listed first and highlighted. */
  focusId?: number | null;
  /** The request whose decision is being saved. */
  busyId: number | null;
  onApprove: (request: BookingApproval) => void;
  onReject: (request: BookingApproval, note: string) => void;
}

/** "Waiting for your approval" (#401 §6): the company's open booking requests, soonest deadline first.
 *  Each request's times render in its own ``site_time_zone`` (the room's site), not the admin's browser. */
export function ApprovalRequestsCard({ requests, total, focusId, busyId, onApprove, onReject }: ApprovalRequestsCardProps) {
  const [rejecting, setRejecting] = useState<BookingApproval | null>(null);
  const [note, setNote] = useState("");
  if (requests.length === 0) return null;
  const waiting = Math.max(total ?? requests.length, requests.length);
  const ordered = focusId ? [...requests].sort((a, b) => Number(b.id === focusId) - Number(a.id === focusId)) : requests;

  return (
    <Card withBorder padding="md" data-testid="approval-requests">
      <Stack gap="sm">
        <Group justify="space-between">
          <Title order={4}>Waiting for your approval</Title>
          <Badge color="orange" variant="light">{waiting}</Badge>
        </Group>
        {waiting > requests.length && (
          <Text fz="sm" c="dimmed">{`Showing the ${requests.length} with the soonest deadlines of ${waiting} waiting.`}</Text>
        )}
        {ordered.map((r) => (
          <Card
            key={r.id}
            withBorder
            padding="sm"
            bg={r.id === focusId ? "var(--mantine-color-orange-light)" : undefined}
            data-testid={`approval-${r.id}`}
          >
            <Stack gap={4}>
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Text fw={500}>{`${r.member_name} · ${r.room_name}`}</Text>
                <Text fz="sm" fw={600} c="orange" flex="none" style={{ whiteSpace: "nowrap" }}>
                  {r.estimate_sek != null ? `about ${formatKr(Number(r.estimate_sek))} kr` : "cost unknown"}
                </Text>
              </Group>
              <Text fz="sm" c="dimmed">{`${dayClock(r.start, r.site_time_zone)}–${clock(r.end, r.site_time_zone)} · ${r.floor_name}`}</Text>
              {r.title && <Text fz="xs" c="dimmed">{r.title}</Text>}
              {r.expires_at && (
                <Text fz="xs" c="dimmed">
                  {lapsesAtStart(r.expires_at, r.start)
                    ? `Decide before it starts at ${clock(r.start, r.site_time_zone)} — otherwise the room is released.`
                    : `Decide by ${dayClock(r.expires_at, r.site_time_zone)} — after that the room is released.`}
                </Text>
              )}
              <Group gap="xs" mt={4}>
                <Button size="xs" color="green" loading={busyId === r.id} onClick={() => onApprove(r)}>
                  Approve
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  color="red"
                  disabled={busyId === r.id}
                  onClick={() => {
                    setNote("");
                    setRejecting(r);
                  }}
                >
                  Reject
                </Button>
              </Group>
            </Stack>
          </Card>
        ))}
      </Stack>
      <Modal opened={rejecting !== null} onClose={() => setRejecting(null)} title="Reject booking request" size="sm">
        <Stack gap="sm">
          {rejecting && (
            <Text fz="sm">{`${rejecting.member_name} · ${rejecting.room_name}, ${dayClock(rejecting.start, rejecting.site_time_zone)}`}</Text>
          )}
          <Textarea
            label="Reason (optional — your colleague sees it)"
            maxLength={255}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setRejecting(null)}>Cancel</Button>
            <Button
              color="red"
              onClick={() => {
                if (rejecting) onReject(rejecting, note.trim());
                setRejecting(null);
              }}
            >
              Reject
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}
